import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { focusHref } from '../components/ScheduledList.jsx';
import { api } from '../api.js';
import { timeAgo } from '../util/time.js';
import { Sr } from '../util/a11y.jsx';
import { STALE_DAYS } from '@hunter/shared/applications';
import EmptyState from '../components/EmptyState.jsx';
import { toast } from '../components/Toasts.jsx';
import { refreshNavCounts } from '../hooks/useNavCounts.js';

// T221 — the morning digest, and its history.
//
// It began as an email and stalled on a credential nobody had. That was the
// wrong shape: the digest is an artefact you read, and email is one way to
// deliver it. Here it is the primary surface — with no SMTP configured every
// digest still exists, still has a history, and the email is simply not sent.
//
// The content is a SNAPSHOT taken that morning, deliberately not re-derived
// from live rows. "What did it tell me on the 3rd" has to keep its answer even
// after those jobs were re-scored, archived or closed; joining back to live
// data would quietly rewrite the past on every scan.
//
// Lives in the shared Layout shell like every other authenticated page.

function DeliveryNote({ digest }) {
  if (digest.sentAt) {
    return (
      <span className="text-xs text-emerald-400" title={new Date(digest.sentAt).toISOString()}>
        emailed {timeAgo(digest.sentAt)}
      </span>
    );
  }
  if (digest.deliveryReason === 'smtp_not_configured') {
    return (
      <span className="text-xs text-slate-500" title="Set SMTP_HOST and friends to have this arrive in your inbox too.">
        not emailed — SMTP not configured
      </span>
    );
  }
  if (digest.deliveryReason === 'digest_disabled') {
    return <span className="text-xs text-slate-500">not emailed — digest disabled</span>;
  }
  if (digest.deliveryReason) {
    // A send that was ATTEMPTED and failed is a different thing from one that
    // was never tried, and the operator should be able to tell them apart.
    // T265(8) — the actual reason (auth rejected, host unreachable, mailbox
    // full) was mouse-only in a title, so the one piece of information that
    // says what to fix was the one piece he could not read on a phone.
    return (
      <span className="text-xs text-amber-400">
        email failed — <span className="text-slate-400">{digest.deliveryReason}</span>
      </span>
    );
  }
  return null;
}

// T265(1) — every item has carried a recommendationId since the digest was
// written, and the only link on it went to the job board. So the surface the
// operator reads first thing in the morning could not put him in front of the
// row it was telling him about: he read a title here and then went hunting for
// it on the dashboard. The focus-rec mechanism already existed (CommandPalette
// dispatches it, DashboardPage listens) — it just was not wired up here.
//
// T269(10) — the Silent list rendered its own inline copy of this markup.
// `variant` covers the three shapes instead.
function Item({ item, variant = 'top', onOpenRec }) {
  const where = [item.city, item.country].filter(Boolean).join(', ');
  const inApp = item.recommendationId ? () => onOpenRec(item, variant) : null;
  return (
    <li className="border-l-2 border-slate-800 py-2 pl-3 hover:border-brand-600">
      <div className="flex flex-wrap items-baseline gap-x-2">
        {item.url ? (
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-medium text-slate-200 hover:text-brand-400"
          >
            {item.title || '(no title)'}
          </a>
        ) : (
          <span className="text-sm font-medium text-slate-200">{item.title || '(no title)'}</span>
        )}
        {variant === 'closing' && (
          <span className="text-xs text-amber-400">
            {item.daysLeft === 0 ? 'closes today' : item.daysLeft === 1 ? 'closes tomorrow' : `closes in ${item.daysLeft} days`}
          </span>
        )}
        {variant === 'top' && <span className="text-xs font-medium text-brand-400">{item.score}</span>}
        {variant === 'silent' && (
          <span className="text-xs text-slate-500">
            {item.daysSilent}d · {item.status}
          </span>
        )}
        {inApp && (
          <button
            type="button"
            onClick={inApp}
            className="text-xs text-slate-500 underline decoration-dotted hover:text-brand-400"
          >
            {variant === 'silent' ? 'open in tracker' : 'open in app'}
          </button>
        )}
      </div>
      <div className="text-xs text-slate-500">
        {item.company}
        {where ? ` · ${where}` : ''}
      </div>
      {item.why && <p className="mt-1 text-xs text-slate-400">{item.why}</p>}
    </li>
  );
}

function DigestBody({ digest, onOpenRec }) {
  const b = digest.brief ?? {};
  // T436 — every number names a list, so every number opens it.
  //
  // Five counts in one line and not one of them was clickable, though each
  // describes a screen that already exists and is one click away. A figure
  // you cannot act on is a figure you learn to skim.
  //
  // `scanned / matched` is the exception and stays plain: it describes what
  // the scan READ, which is not a list anywhere in the product — linking it
  // somewhere approximate would be worse than leaving it as the fact it is.
  const briefLine = [
    { n: b.queueSize ?? 0, label: 'in the queue', to: '/' },
    b.newRecommendations ? { n: b.newRecommendations, label: 'new', to: '/' } : null,
    b.closingSoonCount ? { n: b.closingSoonCount, label: 'closing soon', to: '/' } : null,
    b.openApplications ? { n: b.openApplications, label: 'in flight', to: '/applications' } : null,
    // T290 — `jobsFound` is the post-filter KEEP count, not what was scanned.
    {
      n: null,
      label: b.jobsRaw ? `${b.jobsRaw} scanned, ${b.jobsFound ?? 0} matched` : `${b.jobsFound ?? 0} matched`,
      to: null,
    },
  ].filter(Boolean);
  const pipeline = digest.pipeline ?? { silent: [], silentCount: 0 };

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-xl font-semibold text-slate-100">{digest.day}</h1>
        <DeliveryNote digest={digest} />
      </div>
      <p className="mt-1 text-xs text-slate-500">
        {briefLine.map((part, i) => (
          <span key={i}>
            {i > 0 && ' · '}
            {part.to ? (
              <Link to={part.to} className="underline decoration-dotted transition hover:text-brand-400">
                {part.n} {part.label}
              </Link>
            ) : (
              part.label
            )}
          </span>
        ))}
      </p>

      {/* T418 — the reading, not just the counts.
          This page has always carried numbers and the dashboard has always
          carried the prose, which is gone the next morning. One generator
          (the dashboard's endpoint, on demand and cached 12h) now writes
          its brief onto the day it describes, so the page he actually reads
          — 27 of 27 — keeps the reading that belonged to that day. Absent
          on days the brief was never generated, which is honest: nothing
          was written then. */}
      {b.prose?.headline && (
        <section className="mt-3 rounded-md border border-brand-700 bg-brand-600/5 px-3 py-2">
          <h2 className="text-sm font-medium text-slate-100">{b.prose.headline}</h2>
          {b.prose.summary && <p className="mt-1 max-w-prose text-xs text-slate-300">{b.prose.summary}</p>}
          {b.prose.actions?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {/* `{label, why}`, the same shape MorningBriefPanel renders —
                  the first version of this assumed plain strings and the
                  model rejected every write. */}
              {b.prose.actions.map((a, i) => (
                <li key={i} className="flex items-baseline gap-2 text-xs">
                  <span className="shrink-0 font-mono text-[10px] text-brand-400">{i + 1}.</span>
                  <div className="min-w-0">
                    <p className="text-slate-200">{a.label}</p>
                    {a.why && <p className="text-[11px] italic text-slate-500">{a.why}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {digest.blocked?.reason ? (
        /* T287 — the scan ran and found plenty; it had nothing to rank it
           against. "Nothing new" would be false, and a top three drawn from
           meaningless scores was worse: one real digest opened with "Leitung
           Buchhaltung (m/w/d)" — accounting management, in German — for an
           account whose scan had recorded exactly this reason. */
        <div className="mt-6 rounded-xl border border-amber-800/60 bg-amber-950/20 p-4">
          <p className="text-sm font-medium text-amber-200">Nothing to rank yet</p>
          <p className="mt-1 text-sm text-amber-100/80">{digest.blocked.reason}</p>
          <p className="mt-2 text-xs text-amber-200/60">
            The scan itself ran — {digest.brief?.jobsFound ?? 0} postings matched your filters. Once
            there is something to score them against, they arrive here ranked.
          </p>
        </div>
      ) : digest.quiet ? (
        <p className="mt-6 text-sm text-slate-400">
          Nothing new worth leading with. The scan ran and found nothing above the line — which is a
          result, not a gap. A day missing from this list means the scan itself did not run.
        </p>
      ) : (
        <>
          {digest.top?.length > 0 && (
            <section className="mt-6">
              <h2 className="text-xs uppercase tracking-wide text-slate-500">Top matches</h2>
              <ul className="mt-2 space-y-1">
                {digest.top.map((t, i) => (
                  <Item key={`${t.recommendationId ?? t.title}-${i}`} item={t} onOpenRec={onOpenRec} />
                ))}
              </ul>
            </section>
          )}
          {digest.closingSoon?.length > 0 && (
            <section className="mt-6">
              <h2 className="text-xs uppercase tracking-wide text-slate-500">Closing soon</h2>
              <ul className="mt-2 space-y-1">
                {digest.closingSoon.map((c, i) => (
                  <Item key={`${c.recommendationId ?? c.title}-${i}`} item={c} variant="closing" onOpenRec={onOpenRec} />
                ))}
              </ul>
            </section>
          )}
        </>
      )}
      {/* T223 — the tracker half. OUTSIDE the quiet branch on purpose: a day
          with no new jobs can still be a day with an application that has gone
          quiet, and that is the more actionable of the two. */}
      {pipeline.silent?.length > 0 && (
        <section className="mt-6">
          <h2 className="text-xs uppercase tracking-wide text-slate-500">
            Silent — no reply in {STALE_DAYS}+ days
          </h2>
          <ul className="mt-2 space-y-1">
            {pipeline.silent.map((a, i) => (
              <Item key={`${a.recommendationId ?? a.title}-${i}`} item={a} variant="silent" onOpenRec={onOpenRec} />
            ))}
            {pipeline.silentCount > pipeline.silent.length && (
              <li className="py-1 pl-3 text-xs text-slate-500">
                {/* T436 — this counted rows it would not show and offered no
                    way to see them. The tracker has had a `stale` filter the
                    whole time. */}
                <Link to="/applications?show=stale" className="underline decoration-dotted transition hover:text-brand-400">
                  …and {pipeline.silentCount - pipeline.silent.length} more
                </Link>
              </li>
            )}
          </ul>
        </section>
      )}
    </div>
  );
}

const PAGE = 30;

export default function DigestPage() {
  const navigate = useNavigate();
  const [digests, setDigests] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState(null);
  // T265(7) — the page asked for 60 and dropped the API's `hasMore` on the
  // floor, so at 61 digests the oldest silently ceased to exist. Same lesson as
  // the dashboard's window of 100 (T220): a fixed window with no "more" control
  // is a truncation the operator cannot see.
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [scanning, setScanning] = useState(false);

  const load = useCallback(async () => {
    // T290 — clear the error BEFORE trying again. T249 added a Retry button to
    // this screen and `error` was never reset, so the button fetched, the fetch
    // succeeded, and the operator stayed on "Could not load your digests"
    // forever: the error branch is checked before the data branch, so a
    // populated `digests` could not get past a stale `error`. The only way out
    // was a page reload — which is exactly the dead end T249 was written to
    // remove, still there behind the button that was supposed to fix it.
    setError(null);
    try {
      const body = await api.get(`/digests?limit=${PAGE}`);
      const rows = body.digests ?? [];
      setDigests(rows);
      setHasMore(Boolean(body.hasMore));
      setSelectedId((cur) => cur ?? rows[0]?.id ?? null);
    } catch (e) {
      setError(e?.body?.message || e?.message || 'Could not load digests');
    }
  }, []);

  const loadOlder = useCallback(async () => {
    setLoadingMore(true);
    try {
      const body = await api.get(`/digests?limit=${PAGE}&offset=${digests?.length ?? 0}`);
      setDigests((rows) => [...(rows ?? []), ...(body.digests ?? [])]);
      setHasMore(Boolean(body.hasMore));
    } catch (e) {
      toast(e?.body?.message || e?.message || 'Could not load older digests', { kind: 'error' });
    } finally {
      setLoadingMore(false);
    }
  }, [digests]);

  // T265(6) — the empty state described what a digest is and offered no way to
  // cause one. A digest is written after a completed scan, so that is the CTA.
  const scanNow = useCallback(async () => {
    setScanning(true);
    try {
      const r = await api.post('/scans/trigger', {});
      toast(
        r.alreadyRunning
          ? 'A scan is already running — your digest appears when it finishes.'
          : 'Scan started — your digest is written when it finishes (~30–60s).',
        { kind: 'info' },
      );
    } catch (e) {
      toast(e?.body?.message || e?.message || 'Could not start a scan', { kind: 'error' });
    } finally {
      setScanning(false);
    }
  }, []);

  // T265(1) — put the operator in front of the row the digest is talking about.
  const openRec = useCallback(
    (item, variant) => {
      if (variant === 'silent') {
        // T436 — it said "open in tracker" and opened the tracker, not the
        // application: the answer to "which one?" was for him to find it
        // again among a hundred rows. `?focus=` has existed since T415 and
        // nothing here was using it.
        navigate(focusHref(item.recommendationId));
        return;
      }
      navigate(`/#rec-${item.recommendationId}`);
      window.dispatchEvent(new CustomEvent('hunter:focus-rec', { detail: { id: item.recommendationId } }));
    },
    [navigate],
  );

  useEffect(() => {
    load();
  }, [load]);

  const selected = useMemo(
    () => (digests ?? []).find((d) => d.id === selectedId) ?? null,
    [digests, selectedId],
  );

  // Mark read on open. Optimistic — the badge should clear the instant he looks,
  // and a failed PATCH here is not worth a toast.
  useEffect(() => {
    if (!selected || selected.readAt) return;
    setDigests((rows) => rows.map((d) => (d.id === selected.id ? { ...d, readAt: new Date().toISOString() } : d)));
    // T265(2) — the comment above promised the badge clears "the instant he
    // looks". It did not: the sidebar count is polled on its own 60s timer, so
    // the unread dot sat there for up to a minute after the digest was open.
    api.patch(`/digests/${selected.id}/read`, {}).then(refreshNavCounts).catch(() => {});
  }, [selected]);

  if (error) {
    // T249 — a dead end: the page reported the failure and offered no way out.
    return (
      <EmptyState icon="⚠" title="Could not load your digests" body={error} actions={[{ label: 'Retry', onClick: () => load() }]} />
    );
  }
  if (digests === null) {
    // T265(8) — "Loading…" told the operator nothing about what was arriving.
    // The skeleton mirrors the two-column shape so the load reads as filling in.
    return (
      <div className="mt-6 flex flex-col gap-6 md:flex-row" aria-busy="true">
        <span className="sr-only">Loading your digests…</span>
        <div className="space-y-1.5 md:w-64 md:shrink-0">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-7 animate-pulse rounded-md motion-reduce:animate-none" style={{ background: 'var(--c-surface-2)' }} />
          ))}
        </div>
        <div className="min-w-0 flex-1 space-y-3">
          <div className="h-6 w-40 animate-pulse rounded motion-reduce:animate-none" style={{ background: 'var(--c-surface-2)' }} />
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded motion-reduce:animate-none" style={{ background: 'var(--c-surface-2)' }} />
          ))}
        </div>
      </div>
    );
  }
  if (digests.length === 0) {
    return (
      <EmptyState
        icon="✉"
        title="No digests yet"
        body="One is written after each completed daily scan — the top matches, anything closing soon, and a one-line brief. It is stored here whether or not email is configured."
        actions={[{ label: scanning ? 'starting…' : 'Scan now', onClick: scanNow, disabled: scanning, primary: true }]}
      />
    );
  }

  return (
    <div className="mt-6 flex flex-col gap-6 md:flex-row">
      {/* T265(5) — on a phone the history list came first, so opening the page
          in the morning meant scrolling past every past digest to reach
          today's. The nav is the secondary column; it goes second until there
          is room to put it beside the content. */}
      <nav className="order-2 md:order-1 md:w-64 md:shrink-0" aria-label="Digest history">
        <h2 className="text-xs uppercase tracking-wide text-slate-500">History</h2>
        <ul className="mt-2 space-y-0.5">
          {digests.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                onClick={() => setSelectedId(d.id)}
                aria-current={d.id === selectedId ? 'true' : undefined}
                className={
                  'flex w-full items-baseline gap-2 rounded-md px-2 py-1.5 text-left text-sm transition ' +
                  (d.id === selectedId
                    ? 'bg-slate-800 text-slate-100'
                    : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200')
                }
              >
                {/* T250 — an aria-label on a bare <span> is not a reliable
                    accessible name (no role to hang it on). The dot is
                    decoration; the word travels in the sr-only text. */}
                {!d.readAt && (
                  <>
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" aria-hidden="true" />
                    <Sr>unread </Sr>
                  </>
                )}
                <span className={'shrink-0 font-mono text-xs ' + (d.readAt ? 'text-slate-600' : 'text-slate-400')}>
                  {d.day}
                </span>
                <span className="truncate text-xs text-slate-500">
                  {d.blocked?.reason ? 'not ranked' : d.quiet ? 'quiet' : `${d.top?.length ?? 0} top`}
                </span>
              </button>
            </li>
          ))}
        </ul>
        {hasMore && (
          <button
            type="button"
            onClick={loadOlder}
            disabled={loadingMore}
            className="mt-2 w-full rounded-md border border-slate-800 px-2 py-1.5 text-xs text-slate-400 transition hover:border-brand-600 hover:text-brand-400 disabled:opacity-50"
          >
            {loadingMore ? 'loading…' : 'Load older'}
          </button>
        )}
      </nav>

      <div className="order-1 min-w-0 flex-1 md:order-2">
        {selected && <DigestBody digest={selected} onOpenRec={openRec} />}
      </div>
    </div>
  );
}
